/**
 * Date helpers for DateField and Calendar: native Date plus Intl, no library.
 *
 * Every Date here is a LOCAL calendar day at midnight. "yyyy-MM-dd" strings are
 * read as local days, never through `new Date('2026-10-20')`, which JavaScript
 * reads as UTC midnight and so shows the previous day west of Greenwich.
 */

const MONTH_LONG = new Intl.DateTimeFormat('en-US', { month: 'long' });
const MONTH_SHORT = new Intl.DateTimeFormat('en-US', { month: 'short' });
const WEEKDAY_SHORT = new Intl.DateTimeFormat('en-US', { weekday: 'short' });
const WEEKDAY_LONG = new Intl.DateTimeFormat('en-US', { weekday: 'long' });
const FULL_DATE = new Intl.DateTimeFormat('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

/** Local midnight of the given calendar day. */
export function makeDate(year: number, monthIndex: number, day: number): Date {
  const d = new Date(year, monthIndex, day);
  // Years 0-99 are read as 1900-1999 by the constructor.
  if (year >= 0 && year < 100) d.setFullYear(year);
  return d;
}

export function today(): Date {
  const n = new Date();
  return makeDate(n.getFullYear(), n.getMonth(), n.getDate());
}

export function startOfDay(d: Date): Date {
  return makeDate(d.getFullYear(), d.getMonth(), d.getDate());
}

export function sameDay(a: Date | null | undefined, b: Date | null | undefined): boolean {
  return !!a && !!b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export function addDays(d: Date, n: number): Date {
  return makeDate(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

/** Same day of the month n months on, clamped to the last day (Jan 31 + 1 month = Feb 28/29). */
export function addMonths(d: Date, n: number): Date {
  const first = makeDate(d.getFullYear(), d.getMonth() + n, 1);
  const last = makeDate(first.getFullYear(), first.getMonth() + 1, 0).getDate();
  return makeDate(first.getFullYear(), first.getMonth(), Math.min(d.getDate(), last));
}

export function addYears(d: Date, n: number): Date {
  return addMonths(d, n * 12);
}

/** Sunday on or before the day (US weeks start on Sunday). */
export function startOfWeek(d: Date): Date {
  return addDays(d, -d.getDay());
}

export function endOfWeek(d: Date): Date {
  return addDays(d, 6 - d.getDay());
}

const pad = (n: number, w = 2) => String(n).padStart(w, '0');

/** yyyy-MM-dd, the shape DateBox callers store (dateSerializationFormat="yyyy-MM-dd"). */
export function toIsoDate(d: Date): string {
  return `${pad(d.getFullYear(), 4)}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** MM/DD/YYYY, US display. */
export function formatUs(d: Date): string {
  return `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${pad(d.getFullYear(), 4)}`;
}

export function formatFull(d: Date): string {
  return FULL_DATE.format(d);
}

export function monthName(monthIndex: number, style: 'long' | 'short' = 'long'): string {
  return (style === 'long' ? MONTH_LONG : MONTH_SHORT).format(makeDate(2000, monthIndex, 1));
}

/** Sun..Sat, or Sunday..Saturday. 2026-10-18 is a Sunday. */
export function weekdayNames(style: 'short' | 'long' = 'short'): string[] {
  const f = style === 'short' ? WEEKDAY_SHORT : WEEKDAY_LONG;
  return Array.from({ length: 7 }, (_, i) => f.format(makeDate(2026, 9, 18 + i)));
}

function validDay(y: number, m: number, d: number): Date | null {
  if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(d)) return null;
  if (m < 1 || m > 12 || d < 1 || d > 31 || y < 1 || y > 9999) return null;
  const date = makeDate(y, m - 1, d);
  return date.getMonth() === m - 1 && date.getDate() === d ? date : null;
}

/**
 * A stored value to a local day: "yyyy-MM-dd", "yyyy-MM-ddTHH:mm:ss" (the
 * time is dropped, as SettingsDateInput callers do with split('T')), or a Date.
 * Anything else, including '', is null.
 */
export function toDay(value: string | Date | null | undefined): Date | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : startOfDay(value);
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  return m ? validDay(Number(m[1]), Number(m[2]), Number(m[3])) : null;
}

/**
 * How a two-digit year is read: '2000s' (the default, 10/20/26 is 2026) or
 * 'past' (birth dates: a year that would land after today is read in the
 * 1900s, so 08/10/67 is 1967 while 08/10/19 is still 2019).
 */
export type TwoDigitYear = '2000s' | 'past';

function typedDay(m: string, d: string, y: string, twoDigitYear: TwoDigitYear): Date | null {
  if (y.length !== 2) return validDay(Number(y), Number(m), Number(d));
  const date = validDay(2000 + Number(y), Number(m), Number(d));
  if (date && twoDigitYear === 'past' && date > today()) return validDay(1900 + Number(y), Number(m), Number(d));
  return date;
}

/** The one reading that is a real day, or null when none or more than one is. */
function onlyReading(readings: (Date | null)[]): Date | null {
  const real = readings.filter((d): d is Date => d !== null);
  return real.length === 1 ? real[0] : null;
}

/**
 * What a person typed, as US month/day/year. Accepts 10/20/2026, 10-20-2026,
 * 10.20.2026, 1/5/2026, 10202026 and two-digit years (10/20/26 is 2026, or see
 * TwoDigitYear). Bare 7 digits are M DD YYYY or MM D YYYY (8101967 is
 * 8/10/1967) and bare 6 digits are MM DD YY or M D YYYY (081067, 811967), but
 * only when exactly one reading is a real day: 1121967 could be 1/12/1967 or
 * 11/2/1967, so it is null and the field asks for MM/DD/YYYY. The M D YYYY
 * reading of 6 digits only counts for years 1900-2099, so 122510 is 12/25/10.
 * Returns null for anything that is not a real calendar day (02/30/2026).
 */
export function parseUs(text: string, twoDigitYear: TwoDigitYear = '2000s'): Date | null {
  const t = text.trim();
  if (!t) return null;
  const m = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4})$/.exec(t);
  if (m) return typedDay(m[1], m[2], m[3], twoDigitYear);
  if (/^\d{8}$/.test(t)) return typedDay(t.slice(0, 2), t.slice(2, 4), t.slice(4), twoDigitYear);
  if (/^\d{7}$/.test(t)) {
    const y = t.slice(3);
    return onlyReading([typedDay(t.slice(0, 1), t.slice(1, 3), y, twoDigitYear), typedDay(t.slice(0, 2), t.slice(2, 3), y, twoDigitYear)]);
  }
  if (/^\d{6}$/.test(t)) {
    const y = Number(t.slice(2));
    return onlyReading([
      typedDay(t.slice(0, 2), t.slice(2, 4), t.slice(4), twoDigitYear),
      y >= 1900 && y <= 2099 ? typedDay(t.slice(0, 1), t.slice(1, 2), t.slice(2), twoDigitYear) : null,
    ]);
  }
  return null;
}

/**
 * Slashes put in while a date is typed: "08" becomes "08/", "081" stays, and
 * "0810" becomes "08/10/". A first month digit of 2-9 can only be one month,
 * so "8" becomes "08/" at once; likewise a first day digit of 4-9. A typed
 * "/", "-" or "." ends a one-digit month or day ("8/" is "08/") and is dropped
 * right after a slash that was already there, so it is never doubled.
 * `pad` false (M/D/YYYY fields) keeps one-digit months and days as typed.
 *
 * Only for text typed forward: returns null when the text is not digits and
 * separators, or does not read as the start of a date (13, 00, a fifth year
 * digit), and the caller then leaves the text exactly as typed.
 */
export function autoSlash(text: string, pad = true): string | null {
  if (!/^[\d/.-]*$/.test(text)) return null;
  const parts = ['', '', ''];
  let part = 0;
  const close = () => {
    if (pad && parts[part].length === 1) parts[part] = `0${parts[part]}`;
    part++;
  };
  for (const ch of text) {
    if (ch === '/' || ch === '.' || ch === '-') {
      // Right after a slash (the one put in, or one already typed): drop it.
      if (part > 0 && parts[part] === '') continue;
      if (part === 2 || parts[part] === '' || parts[part] === '0') return null;
      close();
      continue;
    }
    const cur = parts[part];
    if (part === 2) {
      if (cur.length === 4) return null;
      parts[2] = cur + ch;
      continue;
    }
    const highFirst = part === 0 ? '1' : '3';
    if (cur === '') {
      parts[part] = ch;
      if (ch > highFirst) close();
      continue;
    }
    const n = Number(cur + ch);
    if (n < 1 || n > (part === 0 ? 12 : 31)) return null;
    parts[part] = cur + ch;
    close();
  }
  return parts.slice(0, part + 1).join('/');
}

/** Whether the day is inside [min, max]; either bound may be missing. */
export function inRange(d: Date, min: Date | null, max: Date | null): boolean {
  return (!min || d >= min) && (!max || d <= max);
}

export function clampDay(d: Date, min: Date | null, max: Date | null): Date {
  if (min && d < min) return min;
  if (max && d > max) return max;
  return d;
}

/** The 42 days (6 weeks) shown for a month, starting on the Sunday on or before the 1st. */
export function monthGrid(year: number, monthIndex: number): Date[] {
  const start = startOfWeek(makeDate(year, monthIndex, 1));
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
}

/* --- Month-only mode ----------------------------------------------------- */

export function startOfMonth(d: Date): Date {
  return makeDate(d.getFullYear(), d.getMonth(), 1);
}

/** "October 2026", as DateBox's displayFormat "MMMM yyyy". */
export function formatMonthYear(d: Date): string {
  return `${monthName(d.getMonth())} ${pad(d.getFullYear(), 4)}`;
}

/** M/D/YYYY, the shorter US display some screens use (DateBox "M/d/yyyy"). */
export function formatUsShort(d: Date): string {
  return `${d.getMonth() + 1}/${d.getDate()}/${pad(d.getFullYear(), 4)}`;
}

/**
 * A typed month, as the first of that month. Accepts "October 2026",
 * "oct 2026", "Oct. 26", "10/2026", "10-26", "2026-10" and a full date
 * ("10/20/2026" is October 2026). Null when it is not a month.
 */
export function parseMonthYear(text: string): Date | null {
  const t = text.trim().replace(/,/g, ' ').replace(/\s+/g, ' ');
  if (!t) return null;
  // Bare 6 or 7 digits are not read as a full date here: "102026" is not
  // meant as October 20.
  const full = /^\d{6,7}$/.test(t) ? null : parseUs(t);
  if (full) return startOfMonth(full);
  const year = (s: string) => (s.length === 2 ? 2000 + Number(s) : Number(s));
  let m = /^([a-z]+)\.? (\d{2}|\d{4})$/i.exec(t);
  if (m) {
    const name = m[1].toLowerCase();
    if (name.length < 3) return null;
    for (let i = 0; i < 12; i++) {
      if (monthName(i).toLowerCase().startsWith(name)) {
        const y = year(m[2]);
        return y >= 1 && y <= 9999 ? makeDate(y, i, 1) : null;
      }
    }
    return null;
  }
  m = /^(\d{1,2})[/.-](\d{2}|\d{4})$/.exec(t);
  if (m) {
    const mo = Number(m[1]);
    const y = year(m[2]);
    return mo >= 1 && mo <= 12 && y >= 1 ? makeDate(y, mo - 1, 1) : null;
  }
  m = /^(\d{4})-(\d{1,2})$/.exec(t);
  if (m) {
    const mo = Number(m[2]);
    return mo >= 1 && mo <= 12 ? makeDate(Number(m[1]), mo - 1, 1) : null;
  }
  return null;
}

/* --- Time of day (TimeField) --------------------------------------------- */

/** Seconds since midnight. */
export type TimeOfDay = number;

/**
 * A stored time to seconds since midnight: "HH:mm", "HH:mm:ss",
 * "yyyy-MM-ddTHH:mm[:ss]" (the 1900-01-01 sentinel strings Settings stores)
 * or a Date (its local clock time). Anything else is null.
 */
export function toTimeOfDay(value: string | Date | null | undefined): TimeOfDay | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.getHours() * 3600 + value.getMinutes() * 60 + value.getSeconds();
  }
  const m = /(?:^|T)(\d{1,2}):(\d{2})(?::(\d{2}))?/.exec(value);
  if (!m) return null;
  const h = Number(m[1]);
  const mi = Number(m[2]);
  const s = m[3] ? Number(m[3]) : 0;
  return h < 24 && mi < 60 && s < 60 ? h * 3600 + mi * 60 + s : null;
}

/** "9:30 AM", or "9:30:15 AM" with seconds. */
export function formatTime(t: TimeOfDay, withSeconds = false): string {
  const h24 = Math.floor(t / 3600);
  const mi = Math.floor((t % 3600) / 60);
  const s = t % 60;
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${pad(mi)}${withSeconds ? `:${pad(s)}` : ''} ${h24 < 12 ? 'AM' : 'PM'}`;
}

/** "HH:mm", or "HH:mm:ss" with seconds (24-hour, the shape <input type=time> uses). */
export function toTimeString(t: TimeOfDay, withSeconds = false): string {
  const h = Math.floor(t / 3600);
  const mi = Math.floor((t % 3600) / 60);
  return `${pad(h)}:${pad(mi)}${withSeconds ? `:${pad(t % 60)}` : ''}`;
}

/**
 * What a person typed as a time. Accepts "9:30 AM", "9:30am", "9:30 p",
 * "930pm", "9 am", "9:30:15 PM", and 24-hour "21:30" / "0930" when there is
 * no AM/PM ("9:30" is 9:30 AM). Null when it is not a real time.
 */
export function parseTime(text: string): TimeOfDay | null {
  const t = text.trim().toLowerCase().replace(/\./g, '');
  if (!t) return null;
  const m = /^(\d{1,2})(?::?(\d{2}))?(?::(\d{2}))?\s*(a|am|p|pm)?$/.exec(t);
  if (!m) return null;
  let h = Number(m[1]);
  const mi = m[2] ? Number(m[2]) : 0;
  const s = m[3] ? Number(m[3]) : 0;
  if (mi > 59 || s > 59) return null;
  const suffix = m[4];
  if (suffix) {
    if (h < 1 || h > 12) return null;
    if (h === 12) h = 0;
    if (suffix.startsWith('p')) h += 12;
  } else if (h > 23) {
    return null;
  }
  return h * 3600 + mi * 60 + s;
}
